import { useMemo, useState, useCallback } from 'react';
import type { LabelSize, LabelState, LabelType } from './types';

// ============================================================================
// Types
// ============================================================================

export interface UseLabelProps {
  size?: LabelSize;
  labelType?: LabelType;
  state?: LabelState;
  selected?: boolean;
  disabled?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onMouseDown?: () => void;
  onMouseUp?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

export interface LabelSizeConfig {
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  iconSize: number;
  height: number;
  paddingX: number;
  paddingY: number;
  gap: number;
}

export interface LabelStateStyles {
  backgroundColor: string;
  textColor: string;
  iconColor: string;
  borderColor: string;
  cursor: string;
  isHovered: boolean;
  isFocused: boolean;
  isPressed: boolean;
}

export interface UseLabelReturn {
  sizeStyles: LabelSizeConfig;
  stateStyles: LabelStateStyles;
  borderRadius: number;
  isDisabled: boolean;
  computedState: LabelState;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleMouseDown: () => void;
  handleMouseUp: () => void;
  handleFocus: () => void;
  handleBlur: () => void;
}

// ============================================================================
// Size Configuration (matches design system)
// ============================================================================

export const sizeConfig: Record<LabelSize, LabelSizeConfig> = {
  Large: {
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: 0.1,
    iconSize: 24,
    height: 56,
    paddingX: 16,
    paddingY: 16,
    gap: 8,
  },
  Medium: {
    fontSize: 12,
    lineHeight: 16,
    letterSpacing: 0.5,
    iconSize: 24,
    height: 56,
    paddingX: 16,
    paddingY: 16,
    gap: 8,
  },
  Small: {
    fontSize: 11,
    lineHeight: 16,
    letterSpacing: 0.5,
    iconSize: 20,
    height: 48,
    paddingX: 12,
    paddingY: 12,
    gap: 6,
  },
};

// ============================================================================
// Color Configuration (matches design system)
// ============================================================================

// Background colors based on state and selection
export const backgroundColors = {
  enabled: {
    selected: '#E8EEF0', // Primary Container
    unselected: 'transparent',
  },
  hovered: {
    selected: '#d9e3e6', // slightly darker
    unselected: 'rgba(42, 68, 85, 0.08)', // primaryColor-40 at 8%
  },
  focused: {
    selected: '#E8EEF0',
    unselected: 'transparent',
  },
  pressed: {
    selected: '#cdd9de', // darker
    unselected: 'rgba(42, 68, 85, 0.12)', // primaryColor-40 at 12%
  },
  disabled: {
    selected: '#e5e7e9', // neutral-80
    unselected: 'transparent',
  },
};

// Text/Icon colors
export const textColors = {
  enabled: {
    selected: '#223746', // On Primary Container
    unselected: '#474A4C', // On Surface Variant
  },
  hovered: {
    selected: '#223746',
    unselected: '#474A4C',
  },
  focused: {
    selected: '#223746',
    unselected: '#474A4C',
  },
  pressed: {
    selected: '#223746',
    unselected: '#474A4C',
  },
  disabled: {
    selected: '#b6bcc1',
    unselected: '#b6bcc1',
  },
};

// Focus ring color
export const focusRingColor = '#2a4455'; // primaryColor-40

// ============================================================================
// useLabel Hook
// ============================================================================

export const useLabel = ({
  size = 'Medium',
  labelType = 'Round',
  state = 'Enabled',
  selected = false,
  disabled = false,
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  onFocus,
  onBlur,
}: UseLabelProps): UseLabelReturn => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Compute actual state based on props and internal state
  const computedState: LabelState = useMemo(() => {
    if (disabled) {
      return 'Disabled';
    }
    if (state !== 'Enabled') {
      return state;
    }
    if (isPressed) {
      return 'Pressed';
    }
    if (isFocused) {
      return 'Focused';
    }
    if (isHovered) {
      return 'Hovered';
    }
    return 'Enabled';
  }, [disabled, state, isPressed, isFocused, isHovered]);

  const isDisabled = computedState === 'Disabled';

  // Size styles
  const sizeStyles = useMemo(() => sizeConfig[size], [size]);

  // Border radius based on type
  const borderRadius = useMemo(() => {
    return labelType === 'Round' ? 100 : 8;
  }, [labelType]);

  // State-based styles
  const stateStyles: LabelStateStyles = useMemo(() => {
    const stateKey =
      computedState.toLowerCase() as keyof typeof backgroundColors;
    const bgColors = backgroundColors[stateKey] || backgroundColors.enabled;
    const txtColors = textColors[stateKey] || textColors.enabled;

    return {
      backgroundColor: selected ? bgColors.selected : bgColors.unselected,
      textColor: selected ? txtColors.selected : txtColors.unselected,
      iconColor: selected ? txtColors.selected : txtColors.unselected,
      borderColor: computedState === 'Focused' ? focusRingColor : 'transparent',
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      isHovered,
      isFocused,
      isPressed,
    };
  }, [computedState, selected, isDisabled, isHovered, isFocused, isPressed]);

  // Event handlers
  const handleMouseEnter = useCallback(() => {
    if (!isDisabled) {
      setIsHovered(true);
      onMouseEnter?.();
    }
  }, [isDisabled, onMouseEnter]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setIsPressed(false);
    onMouseLeave?.();
  }, [onMouseLeave]);

  const handleMouseDown = useCallback(() => {
    if (!isDisabled) {
      setIsPressed(true);
      onMouseDown?.();
    }
  }, [isDisabled, onMouseDown]);

  const handleMouseUp = useCallback(() => {
    setIsPressed(false);
    onMouseUp?.();
  }, [onMouseUp]);

  const handleFocus = useCallback(() => {
    if (!isDisabled) {
      setIsFocused(true);
      onFocus?.();
    }
  }, [isDisabled, onFocus]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
  }, [onBlur]);

  return {
    sizeStyles,
    stateStyles,
    borderRadius,
    isDisabled,
    computedState,
    handleMouseEnter,
    handleMouseLeave,
    handleMouseDown,
    handleMouseUp,
    handleFocus,
    handleBlur,
  };
};

export default useLabel;
